import React, { useId } from 'react';
import Link from '@amzn/awsui-components-core/link';
import { CTA_PREFIXES, useClickTracking } from '../../hooks/useClickTracking';
import { CtaButton } from '../CtaButton/CtaButton';
import { ctaTargetAttributes, CTA_NEW_TAB_DESCRIPTION, type CtaBehavior } from '../../utils/ctaBehavior';
import type { CtaAction } from '../../utils/ctaAction';
import { StoreBadgeCta, isStoreBadgeCtaType, type StoreBadgeCtaType } from '../StoreBadgeLink/StoreBadgeLink';
import ReactMarkdown from 'react-markdown';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './RichText.scss';

/**
 * RichText - Rich text content block for Polyphemus CMS
 *
 * Renders markdown/HTML content with proper typography hierarchy.
 * Supports headings, paragraphs, lists, check lists, FAQ sections,
 * a single CTA, and embedded media.
 *
 * Check list items are flat, indexed props (checkItem1Text, checkItem2Text, …)
 * reconstructed into an array internally. Supports up to 6 items; a slot
 * renders only when its checkItem{N}Text is set.
 *
 * Typography scale:
 * - Heading 400: 52px / 90% line-height (Ember Modern Display Bold)
 * - Heading 300: 38px / 100% line-height
 * - Heading 200: 28px / 100% line-height
 * - Subhead 300: 24px / 120% line-height (Ember Modern Display Regular)
 * - Subhead 200: 22px / 120% line-height
 * - Subhead 100: 20px / 120% line-height
 * - Body 300: 16px / 150% line-height (Ember Modern Text Regular)
 */

export type RichTextCtaType = 'text-link' | 'primary-button' | 'secondary-button' | 'dark-button' | 'anchor-link' | 'store-badges' | 'app-store' | 'google-play';
export type RichTextFontSize = 'extra-small' | 'small' | 'medium' | 'large';
export type RichTextListColumns = '1' | '2' | '3';

const MAX_LIST_COLUMNS = 3;

/** Resolve an authored column count to a whole number of columns between 1 and 3. */
function toListColumns(value: unknown): number {
  const columns = Math.trunc(Number(value));
  if (!Number.isFinite(columns) || columns < 1) return 1;
  return Math.min(columns, MAX_LIST_COLUMNS);
}

export interface RichTextCtaItem {
  /** CTA display text */
  text: string;
  /**
   * CTA destination URL
   * @polyType Url
   */
  href?: string;
  /**
   * CTA style variant. Badge types are excluded because a badge carries its own artwork
   * and destination, so it cannot render through the text-and-link renderers that consume
   * this shape. `@polyType` is explicit because the extractor resolves same-file unions
   * only, and this one references an imported alias.
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   */
  type?: Exclude<RichTextCtaType, StoreBadgeCtaType>;
  /** Optional ID for analytics */
  id?: string;
  /** Optional ld-append for analytics */
  ldAppendix?: string;
  /**
   * Whether the CTA opens in the current tab or a new one. Explicit `@polyType` for the same
   * reason as `type`: the alias is imported.
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   */
  behavior?: CtaBehavior;
  /** CTA action used by composed blocks. */
  action?: CtaAction;
  /** Popup ID used when the CTA action is Popup. */
  popupTargetId?: string;
}

export interface RichTextCheckListItem {
  /** Check list item text */
  text: string;
}

/** Flat, per-item check list props for slots 1-6. */
export interface RichTextCheckListSlotProps {
  /** @polyLabel Check List Item 1 */
  checkItem1Text?: string;
  /** @polyLabel Check List Item 2 */
  checkItem2Text?: string;
  /** @polyLabel Check List Item 3 */
  checkItem3Text?: string;
  /** @polyLabel Check List Item 4 */
  checkItem4Text?: string;
  /** @polyLabel Check List Item 5 */
  checkItem5Text?: string;
  /** @polyLabel Check List Item 6 */
  checkItem6Text?: string;
}

/** Assemble a check list item from the flat props for a given slot (1-6). */
function getCheckListItem(
  props: RichTextCheckListSlotProps,
  slot: 1 | 2 | 3 | 4 | 5 | 6,
): RichTextCheckListItem | null {
  const text = props[`checkItem${slot}Text`];
  return text ? { text } : null;
}

/**
 * @polySemanticUsage Use for flowing prose. Limit formatting inside content to bold, italic,
 * hyperlinks, and bulleted or numbered lists where the source content is a real list — do not
 * reach for headings or other markdown structure to approximate a layout; structured content
 * belongs in a purpose-built block. Leave listColumns at 1 unless the source shows a bulleted
 * list or check list laid out in columns; then set it to that column count (maximum 3), and only
 * for lists of short items. listColumns applies to the check list and every bulleted list in the
 * block, so keep a long-item list in its own RichText block. Never populate imageUrl, imageAlt,
 * or imageAspectRatio: the fields exist, but migrated content must not embed images inside
 * RichText — place images in an image-capable block instead.
 * Never populate the CTA fields (ctaText, ctaHref, ctaType, ctaId, ctaLdAppendix — and
 * the legacy ctas list where the block template still carries it): CTAs in RichText are
 * deprecated and pending removal. Route any
 * call-to-action to the CTA slots of a purpose-built block (Hero, PreFooter, SplitContent,
 * CardGrid) instead. Inline hyperlinks within the prose remain fine.
 */
export interface RichTextProps extends RichTextCheckListSlotProps {
  /**
   * Markdown content string
   *
   * Safely rendered as React elements using react-markdown (no XSS risk).
   * @polyType RichText
   * @polyHelp Markdown content rendered as React elements
   */
  content: string;

  /**
   * Text size variant for body content
   * @polyOptions Extra Small=extra-small,Small=small,Medium=medium,Large=large
   */
  fontSize?: RichTextFontSize;

  /**
   * Optional single CTA text
   * @polyLabel CTA Text
   */
  ctaText?: string;

  /**
   * Optional single CTA href
   * @polyType Url
   * @polyLabel CTA Link
   */
  ctaHref?: string;

  /**
   * Single CTA style variant
   * @polyLabel CTA Type
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link,App Store + Google Play Badges=store-badges,App Store Badge=app-store,Google Play Badge=google-play
   * @polyHelp The three badge types render the official app store artwork and need no CTA Text or CTA Link.
   */
  ctaType?: RichTextCtaType;

  /**
   * Single CTA button ID
   * @polyLabel CTA ID
   * @polyTranslatable false
   */
  ctaId?: string;

  /**
   * Single CTA ld-append
   * @polyLabel CTA LD Appendix
   * @polyTranslatable false
   */
  ctaLdAppendix?: string;

  /**
   * Whether the single CTA opens in the current tab or a new one
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel CTA Behavior
   * @polyTranslatable false
   */
  ctaBehavior?: CtaBehavior;

  /**
   * Number of columns the check list and every top-level bulleted list in the content
   * flow into (1-3). Items fill top to bottom, then continue in the next column. Three
   * columns step down to two at 767px and below, and every list is a single column at
   * 479px and below.
   * @polyType SelectList
   * @polyOptions 1 column=1,2 columns=2,3 columns=3
   * @polyDefault 1
   * @polyLabel List Columns
   * @polyHelp Applies to the check list and every bulleted list in this block. Items fill down the first column, then continue in the next. Leave at 1 column for a normal list. Use 2 or 3 columns only for lists of short items (a few words each), such as product categories or countries. If a block needs a long list and a short one, put them in separate Rich Text blocks.
   * @polyTranslatable false
   */
  listColumns?: RichTextListColumns;

  /**
   * Optional image URL to embed
   * @polyType Url
   * @polyLabel Image URL
   */
  imageUrl?: string;

  /** Image alt text */
  imageAlt?: string;

  /**
   * Image aspect ratio
   * @polyLabel Image Aspect Ratio
   * @polyOptions 16:9=16/9,2:1=2/1,4:3=4/3
   */
  imageAspectRatio?: '16/9' | '2/1' | '4/3';

  /**
   * Optional additional className
   * @polyIgnore
   */
  className?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

/**
 * MarkdownLink - Wraps markdown anchor tags in a Cloudscape secondary Link
 */
const MarkdownLink: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = ({ href, children }) => {
  // Markdown syntax cannot carry an identifier, so every authored inline link relies on
  // href derivation. Tracking rides onClick, not onFollow: Cloudscape fires onFollow only on a
  // plain left click, so a modifier-click opening a new tab would go unreported while every
  // other CTA's raw <a onClick> reports it. Exactly one of the two, since a plain click fires both.
  const trackClick = useClickTracking(CTA_PREFIXES.richText, { href });

  return (
    <Link href={href ?? ''} variant="secondary" fontSize="inherit" onClick={trackClick}>{children}</Link>
  );
};

/**
 * CheckIcon - SVG check mark icon for check lists
 */
const CheckIcon: React.FC = () => (
  <svg
    className="rich-text__check-icon"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7.5 13.475L4.025 10L2.85 11.175L7.5 15.825L17.5 5.825L16.325 4.65L7.5 13.475Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * CheckList - Renders check list items; column layout comes from the block's listColumns
 */
const CheckList: React.FC<{ items: RichTextCheckListItem[] }> = ({ items }) => (
  <div className="rich-text__checklist">
    {items.map((item, index) => (
      <div key={index} className="rich-text__checklist-item">
        <CheckIcon />
        <div className="rich-text__checklist-text">
          <ReactMarkdown
            components={{
              p: ({ children }) => <>{children}</>,
              a: ({ node, ...props }) => <MarkdownLink {...props} />,
            }}
          >
            {item.text}
          </ReactMarkdown>
        </div>
      </div>
    ))}
  </div>
);

/**
 * SingleCtaRenderer - Renders a single CTA (legacy prop support)
 */
export interface SingleCtaRendererProps {
  /**
   * @polyLabel CTA Text
   */
  ctaText?: string;
  /**
   * @polyLabel CTA Link
   */
  ctaHref?: string;
  /**
   * @polyLabel CTA Type
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link,App Store + Google Play Badges=store-badges,App Store Badge=app-store,Google Play Badge=google-play
   */
  ctaType?: RichTextCtaType;
  /** Single CTA button ID (legacy) */
  ctaId?: string;
  /** Single CTA ld-append for analytics (legacy) */
  ctaLdAppendix?: string;
  /** Whether the CTA opens in the current tab or a new one; ignored by badge types */
  ctaBehavior?: CtaBehavior;
}

const SingleCtaRenderer = ({ ctaText, ctaHref, ctaType = 'text-link', ctaId, ctaLdAppendix, ctaBehavior }: SingleCtaRendererProps) => {
  // A store badge supplies its own artwork, label, and destination, so it renders
  // without the authored text and link every other CTA type requires.
  if (isStoreBadgeCtaType(ctaType)) {
    return <StoreBadgeCta ctaType={ctaType} id={ctaId} ldAppendix={ctaLdAppendix} />;
  }

  if (!ctaText || !ctaHref) return null;

  switch (ctaType) {
    case 'text-link':
    case 'anchor-link':
      return <TextLinkCta href={ctaHref} behavior={ctaBehavior} variant="primary">{ctaText}</TextLinkCta>;
    case 'primary-button':
      return (
        <CtaButton href={ctaHref} id={ctaId} ldAppendix={ctaLdAppendix} behavior={ctaBehavior}>
          {ctaText}
        </CtaButton>
      );
    case 'secondary-button':
      return (
        <CtaButton
          colorVariant="secondary"
          href={ctaHref}
          id={ctaId}
          ldAppendix={ctaLdAppendix}
          behavior={ctaBehavior}
        >
          {ctaText}
        </CtaButton>
      );
    case 'dark-button':
      return (
        <CtaButton
          colorVariant="contrast"
          href={ctaHref}
          id={ctaId}
          ldAppendix={ctaLdAppendix}
          behavior={ctaBehavior}
        >
          {ctaText}
        </CtaButton>
      );
    default:
      return <TextLinkCta href={ctaHref} behavior={ctaBehavior}>{ctaText}</TextLinkCta>;
  }
};

interface TextLinkCtaProps {
  href: string;
  behavior?: CtaBehavior;
  variant?: 'primary';
  children: React.ReactNode;
}

/** Cloudscape Link with the same new-tab target/rel and screen-reader description CtaButton and ArrowLink carry. */
const TextLinkCta = ({ href, behavior, variant, children }: TextLinkCtaProps) => {
  const generatedId = useId();
  const newTabDescriptionId = behavior === 'newTab' ? `${generatedId}-new-tab` : undefined;

  return (
    <>
      <Link
        href={href}
        variant={variant}
        fontSize="inherit"
        {...ctaTargetAttributes(behavior)}
        nativeAttributes={{ 'aria-describedby': newTabDescriptionId }}
      >
        {children}
      </Link>
      {newTabDescriptionId && (
        <span id={newTabDescriptionId} className="poly-visually-hidden">{CTA_NEW_TAB_DESCRIPTION}</span>
      )}
    </>
  );
};

export const RichText: React.FC<RichTextProps> = ({
  content,
  fontSize = 'medium',
  ctaText,
  ctaHref,
  ctaType = 'text-link',
  ctaId,
  ctaLdAppendix,
  ctaBehavior,
  listColumns,
  imageUrl,
  imageAlt = '',
  imageAspectRatio = '2/1',
  className,
  anchorId,
  ...slotProps
}) => {
  const resolvedListColumns = toListColumns(listColumns);
  const classNames = [
    'rich-text',
    `rich-text--${fontSize}`,
    resolvedListColumns > 1 && `rich-text--list-cols-${resolvedListColumns}`,
    className,
  ].filter(Boolean).join(' ');

  // Reassemble the flat checkItem{N}Text slots into the array CheckList
  // consumes. PolyC's field editor authors flat primitives only, so the
  // CMS-facing surface is indexed props rather than an array.
  const checkListItems = ([1, 2, 3, 4, 5, 6] as const)
    .map(slot => getCheckListItem(slotProps, slot))
    .filter((item): item is RichTextCheckListItem => item !== null);

  return (
    <div className={classNames} {...blockAnchorAttributes(anchorId)}>
      <div className="rich-text__wrapper">
        {/* Main markdown content */}
        <div className="rich-text-content">
          <ReactMarkdown
            components={{
              a: ({ node, ...props }) => <MarkdownLink {...props} />
            }}
          >
            {content}
          </ReactMarkdown>
        </div>

        {/* Check list section */}
        {checkListItems.length > 0 && (
          <CheckList items={checkListItems} />
        )}

        {/* Single CTA */}
        <SingleCtaRenderer
          ctaText={ctaText}
          ctaHref={ctaHref}
          ctaType={ctaType}
          ctaId={ctaId}
          ctaLdAppendix={ctaLdAppendix}
          ctaBehavior={ctaBehavior}
        />

        {/* Embedded image */}
        {imageUrl && (
          <div
            className="rich-text__image"
            style={{ aspectRatio: imageAspectRatio }}
          >
            <img src={imageUrl} alt={imageAlt} />
          </div>
        )}
      </div>
    </div>
  );
};
